import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Instagram } from 'lucide-react';
import { toast } from 'sonner';
import { fetchList } from '@/lib/api';
import { Logo } from '@/components/Cards';
import { SpotifyIcon, AppleMusicIcon, XIcon, YouTubeIcon, TikTokIcon } from '@/components/BrandIcons';
import { useContent } from '@/lib/useContent';

export default function Footer() {
  const content = useContent();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    // refetch on route change + window focus so admin edits appear without a hard reload
    const refresh = () => fetchList('/settings').then(setSettings).catch(() => {});
    refresh();
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [location.pathname]);

  const socials = [
    { key: 'instagram', label: 'Instagram', url: settings?.instagram_url, Icon: Instagram, hover: 'hover:border-[#E1306C] hover:bg-[#E1306C]' },
    { key: 'x', label: 'X', url: settings?.x_url, Icon: XIcon, hover: 'hover:border-brayo-charcoal hover:bg-brayo-charcoal' },
    { key: 'youtube', label: 'YouTube', url: settings?.youtube_url, Icon: YouTubeIcon, hover: 'hover:border-[#FF0000] hover:bg-[#FF0000]' },
    { key: 'spotify', label: 'Spotify', url: settings?.spotify_url, Icon: SpotifyIcon, hover: 'hover:border-[#1DB954] hover:bg-[#1DB954]' },
    { key: 'tiktok', label: 'TikTok', url: settings?.tiktok_url, Icon: TikTokIcon, hover: 'hover:border-[#FE2C55] hover:bg-[#FE2C55]' },
  ].filter((s) => s.url);

  return (
    <footer data-testid="site-footer" className="border-t border-brayo-line bg-brayo-paper">
      <div className="mx-auto max-w-[1500px] px-5 py-16 md:px-10 md:py-20">
        <div className="grid gap-14 lg:grid-cols-[1.5fr_1fr_1.3fr]">
          <div>
            <Link to="/" data-testid="footer-logo-link" aria-label="Brayo Records home">
              <Logo className="h-14 md:h-20" />
            </Link>
            <p className="tagline-serif mt-5 max-w-sm text-lg leading-snug text-brayo-charcoal md:text-xl">
              {content.footer_tagline || 'Independent record label developing the next generation of music.'}
            </p>
          </div>

          <div>
            <p className="meta-label mb-5 text-brayo-blue">Follow</p>
            <div className="flex flex-wrap gap-3">
              {socials.map(({ key, label, url, Icon, hover }) => (
                <a
                  key={key}
                  data-testid={`footer-social-link-${key}`}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  title={label}
                  className={`flex h-11 w-11 items-center justify-center rounded-full border border-brayo-line text-brayo-charcoal transition-all duration-300 hover:-translate-y-0.5 hover:text-white ${hover}`}
                >
                  <Icon className="h-[18px] w-[18px]" />
                </a>
              ))}
              {socials.length === 0 && <p className="text-sm text-brayo-dim">Coming soon.</p>}
            </div>
          </div>

          <div>
            <p className="meta-label mb-5 text-brayo-blue">Newsletter</p>
            <p className="mb-4 text-sm text-brayo-mute">{content.footer_news_copy || 'Releases, tour dates and label news. No noise.'}</p>
            <form
              data-testid="newsletter-form"
              onSubmit={(e) => { e.preventDefault(); if (email) { toast.success('You are on the list.'); setEmail(''); } }}
              className="flex border border-brayo-line bg-white"
            >
              <input
                data-testid="newsletter-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="w-full bg-transparent px-4 py-3 text-base md:text-sm text-brayo-charcoal placeholder:text-brayo-dim focus:outline-none"
              />
              <button data-testid="newsletter-submit-btn" type="submit" className="meta-label shrink-0 bg-brayo-blue px-4 text-white transition-colors hover:bg-brayo-charcoal">
                Join
              </button>
            </form>
            <p className="meta-label mt-4 text-brayo-dim">{content.footer_note || 'One email a month, at most.'}</p>
          </div>
        </div>

        <div className="mt-16 flex flex-col gap-4 border-t border-brayo-line pt-8 md:flex-row md:items-center md:justify-between">
          <p className="meta-label text-brayo-dim">{content.footer_copyright || '© 2026 Brayo Records. All rights reserved.'}</p>
          <Link data-testid="footer-admin-link" to="/admin" className="meta-label text-brayo-dim transition-colors hover:text-brayo-blue">Label Login</Link>
        </div>
      </div>
    </footer>
  );
}